/**
 * The knowledge packs in force for a project, with their provenance and the optional "Adopt pack vX" (DQ rule
 * governance, decision 8). Pack rules need no approval and run either way: adopting only records, for an auditor,
 * that the client accepted the pack. A Data Steward's action (rules:adopt_pack).
 */
import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Package } from "lucide-react";

import { rulesApi, type KnowledgePack } from "../../api/rules";
import type { ActionGate } from "../../lib/rulesPermissions";
import { GatedButton } from "./RulesPermissionNotice";

export function KnowledgePacksPanel({
  projectId,
  adoptGate,
  nameOf,
}: {
  projectId: string;
  adoptGate: ActionGate;
  /** Display name for a user id (falls back to the id). */
  nameOf?: (userId: string) => string;
}) {
  const [packs, setPacks] = useState<KnowledgePack[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setError(null);
    try {
      setPacks(await rulesApi.packs.list(projectId));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (packs.length === 0 && !error) return null;

  const adopt = async (pack: KnowledgePack) => {
    const key = `${pack.pack_name}@${pack.pack_version}`;
    setError(null);
    setBusy(key);
    try {
      await rulesApi.packs.adopt(
        projectId,
        pack.pack_name,
        pack.pack_version,
        note.trim(),
      );
      setPending(null);
      setNote("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      aria-label="Knowledge packs"
      className="glass-surface rounded-xl p-4 space-y-2"
    >
      <div className="flex items-center gap-2 text-sm text-white/85">
        <Package className="w-4 h-4 text-[var(--primary)]" aria-hidden />
        Knowledge packs in force
        <span className="text-[11px] text-white/45">
          (ZTD standard rules: no approval needed; adopting records the
          client&apos;s acceptance)
        </span>
      </div>
      {error && <div className="text-xs text-red-300">{error}</div>}
      <ul className="space-y-1.5">
        {packs.map((pack) => {
          const key = `${pack.pack_name}@${pack.pack_version}`;
          return (
            <li
              key={key}
              className="flex items-center justify-between gap-3 text-xs text-white/75"
            >
              <span>
                <span className="font-mono">{pack.pack_name}</span> v
                {pack.pack_version} · {pack.rule_count} rules
              </span>
              {pack.adoption ? (
                <span className="inline-flex items-center gap-1 text-emerald-300">
                  <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
                  Adopted by{" "}
                  {nameOf
                    ? nameOf(pack.adoption.adopted_by)
                    : pack.adoption.adopted_by}
                  {pack.adoption.adopted_at
                    ? ` on ${new Date(pack.adoption.adopted_at).toLocaleDateString()}`
                    : ""}
                </span>
              ) : pending === key ? (
                <span className="inline-flex items-center gap-1.5">
                  <input
                    aria-label="Adoption note (optional)"
                    placeholder="Note (optional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-[11px] text-white"
                  />
                  <button
                    onClick={() => void adopt(pack)}
                    disabled={busy === key}
                    className="text-[11px] px-2 py-1 rounded-md bg-[var(--primary)]/20 text-[var(--primary)] disabled:opacity-40"
                  >
                    {busy === key ? "Adopting…" : "Confirm adoption"}
                  </button>
                  <button
                    onClick={() => setPending(null)}
                    className="text-[11px] px-2 py-1 rounded-md text-white/60 hover:bg-white/5"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <GatedButton
                  gate={adoptGate}
                  onClick={() => {
                    setPending(key);
                    setNote("");
                  }}
                  title={`Records that the client accepts these ${pack.rule_count} rules; does not change what runs`}
                  className="text-[11px] px-2 py-1 rounded-md border border-[var(--primary)]/40 text-[var(--primary)] hover:bg-[var(--primary)]/10"
                >
                  {`Adopt v${pack.pack_version}`}
                </GatedButton>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
